import {
  Box,
  Divider,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName, BannerCouponName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCountDown } from "features/Home/Banner/useCountdown";
import {
  formatEpochTimestamp,
  getHoursInFuture,
  isTimeToday,
} from "utils/time";
import { trackEvent } from "utils/tracking";

import { StripeInvoiceLineItem, TabOptions } from "../schema";
import styles from "../styles";
import { useCheckout } from "../useCheckout";
import BillingCycleSwitch from "./BillingCycleSwitch";
import CrossSells from "./CrossSells";
import PromotionalCode from "./PromotionalCode";
import summaryStyles from "./orderSummaryStyles";

interface Props {
  displayName: string | undefined;
  productId: string;
  totalDue: number;
}

const InvoicePreview = ({ displayName, productId, totalDue }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { tab, setTab, subscription, plans } = useContext(SubscriptionContext);
  const { display, createTimer } = useCountDown();
  const { getPromoCodeByName } = useCheckout();
  const {
    applyAddOnLoading,
    invoice,
    customer,
    setPlan,
    plan,
    splashPageSource,
    invoiceLoading,
  } = useContext(CheckoutContext);
  const [hideTab, setHideTab] = useState(false);

  const { applyAddOnItem, removePromoCode } = useCheckout();

  // Variables
  const hasDiscount = invoice?.discounts?.find(
    (x) => x.promotion_code && x.promotion_code?.length > 0,
  );

  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const taxAmount = invoice?.total_taxes?.[0]?.amount;

  const displayVaBundle =
    (plan === Plans.VA ||
      plan === Plans.VA_YEARLY ||
      plan === Plans.VA_QUARTERLY) &&
    splashPageSource !== SplashPage.va_bundled;

  const canRemoveCoupon =
    !splashPageSource ||
    ![SplashPage.ooo, SplashPage.pro_bundle]?.includes(splashPageSource);

  const getItemName = (row: StripeInvoiceLineItem) => {
    return row?.metadata?.item_name;
  };

  const isOnSameTier = () => {
    const currentPlanTier = plans?.[plan as Plans]?.tier;
    return Boolean(
      subscription?.isSubscribed &&
        currentPlanTier !== undefined &&
        subscription?.subscription?.planTier === currentPlanTier,
    );
  };

  const renderDescription = (description: string) => {
    if (plans && plan && plans[plan as Plans]?.price_annotation) {
      return `${description} ${plans[plan as Plans]?.price_annotation}`;
    }
    if (description.startsWith("1 × ")) {
      return description.slice(4);
    }
    return description;
  };

  const renderTabLogic = () => {
    if (!plan || !plans) return;

    const isSameTier = isOnSameTier();
    if (
      isSameTier &&
      subscription?.subscription?.planName &&
      subscription?.subscription?.planName in TabOptions
    ) {
      // @ts-ignore
      const applicablePlan = TabOptions[subscription.subscription.planName];
      if (plan !== applicablePlan) {
        setPlan(applicablePlan);
      }
      if (
        applicablePlan?.includes("yearly") &&
        tab !== SubscriptionTab.yearly
      ) {
        setTab(SubscriptionTab.yearly);
      } else {
        setTab(SubscriptionTab.monthly);
      }
      setHideTab(true);
      return;
    }

    const isYearlyPlan =
      plan &&
      [Plans.VA_YEARLY, Plans.PRO_YEARLY, Plans.AUTOPILOT_YEARLY]?.includes(
        plan as Plans,
      );
    if (isYearlyPlan && tab !== SubscriptionTab.yearly) {
      setTab(SubscriptionTab.yearly);
    }

    const hideTabsSplashSource = [
      SplashPage.ooo,
      SplashPage.pro_bundle,
      SplashPage.va_money_back,
      SplashPage.annual,
    ];
    const hideTab =
      (splashPageSource && hideTabsSplashSource.includes(splashPageSource)) ||
      !(plan && plan in TabOptions);
    setHideTab(Boolean(hideTab));
  };

  useEffect(() => {
    renderTabLogic();
    if (
      plan === Plans.VA_YEARLY &&
      splashPageSource === SplashPage.va_money_back
    ) {
      const bannerPromoCode = getPromoCodeByName(
        BannerCouponName.money_back_guarantee,
      );
      if (bannerPromoCode) {
        const countdownDate = getHoursInFuture(10);
        createTimer(bannerPromoCode, countdownDate?.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, splashPageSource, Boolean(plans)]);

  const getNextPayDate = () => {
    const nextPaymentDate =
      invoice?.next_payment_attempt || invoice?.period_start;
    if (!nextPaymentDate) return "";
    const isTimeStampToday = isTimeToday(nextPaymentDate);
    if (isTimeStampToday) {
      return <>Total due today</>;
    } else {
      return `Total due on ${formatEpochTimestamp({
        epochTimestamp: nextPaymentDate,
        addYear: true,
      })}`;
    }
  };

  const discountAmount =
    Number(invoice?.total_discount_amounts?.[0]?.amount || 0) / 100;
  const originalAmount = (discountAmount || 0) + totalDue;

  const sortedInvoiceLines = [...(invoice?.lines?.data || [])].sort((a, b) => {
    const aIsCustomerSupport =
      a?.metadata?.item_name === AddOnItemName.customer_support;
    const bIsCustomerSupport =
      b?.metadata?.item_name === AddOnItemName.customer_support;
    if (aIsCustomerSupport === bIsCustomerSupport) return 0;
    return aIsCustomerSupport ? 1 : -1;
  });

  return (
    <Box>
      {!hideTab && <BillingCycleSwitch />}

      <Box sx={{ mt: 2 }}>
        {sortedInvoiceLines.map((row, index) => (
          <Box
            key={index}
            sx={[summaryStyles.line, { alignItems: "flex-start" }]}
          >
            <Box sx={summaryStyles.lineLabel}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography component="span" sx={summaryStyles.lineTitle}>
                  {renderDescription(row.description)}
                </Typography>

                {plan !== AddOnItemName.email_volume_monthly &&
                  plan !== AddOnItemName.email_volume_yearly &&
                  plan !== AddOnItemName.pitching_templates &&
                  getItemName(row) && (
                    <Tooltip title="Remove add-on" placement="right">
                      <Box
                        component="span"
                        onClick={async (e) => {
                          e.stopPropagation();
                          trackEvent(
                            "Invoice Preview - Remove Add-On Clicked",
                            {
                              "Add On Item Name": getItemName(row),
                            },
                          );
                          await applyAddOnItem({
                            name: getItemName(row) as AddOnItemName,
                            quantity: 0,
                          });
                        }}
                        sx={styles.deleteAddOnButton(
                          applyAddOnLoading === getItemName(row),
                        )}
                      >
                        <Box
                          component="i"
                          className={
                            applyAddOnLoading === getItemName(row)
                              ? "fa-regular fa-spinner-third fa-spin"
                              : "fa-regular fa-xmark"
                          }
                          sx={{ fontSize: 9 }}
                        />
                      </Box>
                    </Tooltip>
                  )}
              </Box>

              {getItemName(row) === AddOnItemName.customer_support && (
                <Typography sx={{ fontSize: 10, color: grey[500] }}>
                  Get a personal review from our team to make sure your account
                  is set up properly.
                </Typography>
              )}
              {plan === AddOnItemName.custom_email && index === 1 && (
                <ul
                  style={{
                    color: grey[500],
                    margin: 0,
                    marginTop: "4px",
                    paddingLeft: "20px",
                  }}
                >
                  <li>Gmail inbox with your domain</li>
                  <li>Monthly hosting</li>
                  <li>Deliverability setup (SPF, DKIM, DMARC)</li>
                </ul>
              )}
            </Box>

            <Typography
              sx={
                row.amount < 0
                  ? summaryStyles.lineAmountNeg
                  : summaryStyles.lineAmount
              }
            >
              {row.amount < 0
                ? `−$${Math.abs(row.amount / 100).toFixed(2)}`
                : `$${Number(row.amount / 100).toFixed(2)}`}
            </Typography>
          </Box>
        ))}

        {displayVaBundle && (
          <Box sx={summaryStyles.line}>
            <Typography sx={summaryStyles.lineTitle}>
              300+ automated pitches/month
            </Typography>
            <Typography sx={summaryStyles.lineAmountFree}>$0.00</Typography>
          </Box>
        )}

        {isOOOFlow && currentOrg?.isVaUser && (
          <Box sx={summaryStyles.line}>
            <Typography sx={summaryStyles.lineTitle}>
              You already subscribed to Virtual Assistant
            </Typography>
            <Typography sx={summaryStyles.lineAmountFree}>$0.00</Typography>
          </Box>
        )}

        {taxAmount && taxAmount > 0 ? (
          <Box sx={summaryStyles.line}>
            <Typography sx={summaryStyles.lineTitle}>
              Tax ({customer?.address?.country})
            </Typography>
            <Typography sx={summaryStyles.lineAmount}>
              ${Number(taxAmount / 100).toFixed(2)}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {invoice?.total_discount_amounts?.map((row, index) => (
        <Box key={index} sx={summaryStyles.line}>
          <Box
            role="button"
            onClick={() => {
              if (canRemoveCoupon) {
                removePromoCode();
              }
            }}
            sx={[
              summaryStyles.promoChip,
              { cursor: canRemoveCoupon ? "pointer" : "default" },
            ]}
          >
            <Box component="i" className="fa-solid fa-gift" />
            {invoice?.discounts?.[index]?.coupon?.name}
            {canRemoveCoupon && invoice?.discounts?.[index]?.promotion_code && (
              <Box component="i" className="fa-regular fa-xmark" />
            )}
          </Box>
          <Typography sx={summaryStyles.lineAmountNeg}>
            −${Number(row.amount / 100).toFixed(2)}
          </Typography>
        </Box>
      ))}

      {invoice && invoice?.starting_balance !== 0 && (
        <Box sx={summaryStyles.line}>
          <Typography sx={summaryStyles.lineTitle}>Credit Balance</Typography>
          <Typography sx={summaryStyles.lineAmountNeg}>
            −${Number(Math.abs(invoice?.starting_balance / 100)).toFixed(2)}
          </Typography>
        </Box>
      )}

      <Divider sx={summaryStyles.divider} />

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pt: 1.75,
        }}
      >
        {(invoice?.next_payment_attempt || invoice?.period_start) && (
          <Typography sx={summaryStyles.totalLabel}>
            {getNextPayDate()}
          </Typography>
        )}
        <Box sx={{ textAlign: "right" }}>
          {invoiceLoading ? (
            <Skeleton animation="wave" width={150} height={30} />
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "flex-end",
                }}
              >
                <Box component="span" sx={summaryStyles.totalCurrencyPrefix}>
                  $
                </Box>
                <Box component="span" sx={summaryStyles.totalAmount}>
                  {totalDue.toFixed(2)}
                </Box>
                <Box component="span" sx={summaryStyles.totalCurrencySuffix}>
                  {invoice?.currency}
                </Box>
              </Box>

              {originalAmount && originalAmount > totalDue && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "flex-end",
                    mt: 1,
                  }}
                >
                  <Typography sx={summaryStyles.wasPrice}>
                    ${originalAmount.toFixed(2)}
                  </Typography>
                  <Box sx={summaryStyles.saveBadge}>
                    Save ${(originalAmount - totalDue).toFixed(2)}
                  </Box>
                </Box>
              )}
              {display && splashPageSource === SplashPage.va_money_back && (
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "info.main",
                    textTransform: "none",
                    mt: 1,
                  }}
                >
                  ⏳ Additional 10% off for the next <strong>{display}</strong>
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      {!hasDiscount && (
        <Grid container justifyContent="flex-end">
          <PromotionalCode productId={productId} />
        </Grid>
      )}
      {!isMobileScreen && (
        <Box sx={{ mt: 4 }}>
          <CrossSells />
        </Box>
      )}
    </Box>
  );
};

export default InvoicePreview;
